import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Plane, FileText, AlertCircle, TrendingUp,
  Euro, Receipt, Clock, CheckCircle, ArrowRight, Plus,
  BarChart2, RefreshCw, CreditCard, ShieldAlert,
  FileSpreadsheet, Stamp, Users, Inbox, ChevronDown,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENT_QUARTER = `Q${Math.floor(new Date().getMonth() / 3) + 1}`;
const CURRENT_YEAR = new Date().getFullYear();

const fmt = (n) =>
  `€${Number(n || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2 })}`;

function SectionLabel({ children }) {
  return (
    <p className="text-[11px] font-bold uppercase tracking-widest text-purple-400 mb-2.5 flex items-center gap-1.5">
      <span className="inline-block w-3 h-px bg-purple-300" />
      {children}
    </p>
  );
}

// status: "neutral" | "ok" | "warn" | "alert"
function StatCard({ label, value, sub, icon: Icon, status = "neutral", onClick }) {
  const styles = {
    neutral: {
      card:    "bg-white border-slate-200 hover:border-purple-200",
      accent:  "border-l-purple-300",
      iconBg:  "bg-purple-50",
      iconFg:  "text-purple-300",
      dot:     "bg-slate-300",
      sub:     "text-slate-400",
      value:   "text-slate-800",
    },
    ok: {
      card:    "bg-emerald-50/60 border-emerald-200 hover:border-emerald-300",
      accent:  "border-l-emerald-400",
      iconBg:  "bg-emerald-100",
      iconFg:  "text-emerald-500",
      dot:     "bg-emerald-400",
      sub:     "text-emerald-600",
      value:   "text-slate-800",
    },
    warn: {
      card:    "bg-amber-50/70 border-amber-200 hover:border-amber-300",
      accent:  "border-l-amber-400",
      iconBg:  "bg-amber-100",
      iconFg:  "text-amber-500",
      dot:     "bg-amber-400",
      sub:     "text-amber-700",
      value:   "text-amber-800",
    },
    alert: {
      card:    "bg-red-50/70 border-red-200 hover:border-red-300",
      accent:  "border-l-red-400",
      iconBg:  "bg-red-100",
      iconFg:  "text-red-500",
      dot:     "bg-red-400",
      sub:     "text-red-600",
      value:   "text-red-700",
    },
  }[status] || {};

  return (
    <div
      className={`border rounded-xl p-4 border-l-[3px] transition-all ${styles.card} ${styles.accent} ${
        onClick ? "cursor-pointer hover:shadow-sm" : ""
      }`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide leading-none mb-2.5">
            {label}
          </p>
          <p className={`text-2xl font-bold leading-none ${styles.value}`}>{value}</p>
          {sub && (
            <div className="flex items-center gap-1.5 mt-2">
              <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${styles.dot}`} />
              <p className={`text-[11px] truncate ${styles.sub}`}>{sub}</p>
            </div>
          )}
        </div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${styles.iconBg}`}>
          <Icon className={`w-4 h-4 ${styles.iconFg}`} />
        </div>
      </div>
    </div>
  );
}

function ActionItem({ type, name, reason, amount, onClick }) {
  const cfg = {
    ticketing:  { border: "border-l-violet-400", bg: "bg-violet-50/50 hover:bg-violet-50", tag: "bg-violet-100 text-violet-700" },
    payment:    { border: "border-l-amber-400",  bg: "bg-amber-50/50 hover:bg-amber-50",  tag: "bg-amber-100 text-amber-700" },
    supplier:   { border: "border-l-orange-400", bg: "bg-orange-50/50 hover:bg-orange-50",tag: "bg-orange-100 text-orange-700" },
    receipt:    { border: "border-l-slate-300",  bg: "bg-slate-50 hover:bg-white",         tag: "bg-slate-100 text-slate-500" },
    vat:        { border: "border-l-red-400",    bg: "bg-red-50/50 hover:bg-red-50",       tag: "bg-red-100 text-red-700" },
    correction: { border: "border-l-amber-500",  bg: "bg-amber-50/50 hover:bg-amber-50",  tag: "bg-amber-100 text-amber-800" },
    visa:       { border: "border-l-blue-400",   bg: "bg-blue-50/50 hover:bg-blue-50",     tag: "bg-blue-100 text-blue-700" },
  }[type] || { border: "border-l-slate-300", bg: "bg-slate-50 hover:bg-white", tag: "bg-slate-100 text-slate-500" };

  const typeLabel = {
    ticketing: "Ticketing", payment: "Payment", supplier: "Supplier",
    receipt: "Receipt", vat: "VAT", correction: "Correction", visa: "Visa",
  }[type] || type;

  return (
    <div
      className={`flex items-center gap-3 pl-3 pr-3 py-2.5 rounded-lg border-l-[3px] border border-slate-100/80 transition-colors cursor-pointer ${cfg.border} ${cfg.bg}`}
      onClick={onClick}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${cfg.tag}`}>
            {typeLabel}
          </span>
          <span className="text-sm font-medium text-slate-800 truncate">{name}</span>
        </div>
        <p className="text-xs text-slate-400 mt-0.5 truncate">{reason}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {amount != null && (
          <span className="text-xs font-semibold text-slate-600 font-mono">{fmt(amount)}</span>
        )}
        <ArrowRight className="w-3.5 h-3.5 text-slate-300" />
      </div>
    </div>
  );
}

function FinanceRow({ label, value, bold, color, highlight }) {
  const val = typeof value === "number" ? fmt(value) : (value || "—");
  return (
    <div
      className={`flex items-center justify-between py-2 ${
        highlight
          ? "px-3 -mx-3 rounded-lg my-0.5 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-100"
          : "border-b border-slate-100 last:border-0"
      }`}
    >
      <span className={`text-sm ${bold ? "font-semibold text-slate-800" : "text-slate-500"}`}>
        {label}
      </span>
      <span
        className={`text-sm font-mono font-semibold ${
          color === "green" ? "text-emerald-600"
          : color === "red" ? "text-red-600"
          : bold ? "text-slate-800"
          : "text-slate-600"
        }`}
      >
        {val}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [bookings, setBookings]             = useState([]);
  const [visaRequests, setVisaRequests]     = useState([]);
  const [flightRequests, setFlightRequests] = useState([]);
  const [visaQuotes, setVisaQuotes]         = useState([]);
  const [expenses, setExpenses]             = useState([]);
  const [serviceEntries, setServiceEntries] = useState([]);
  const [vatCorrections, setVatCorrections]         = useState([]);
  const [finance, setFinance]                       = useState(null);
  const [loading, setLoading]                       = useState(true);
  const [lastRefresh, setLastRefresh]               = useState(null);

  const [financeOpen, setFinanceOpen] = useState(() => {
    try { return localStorage.getItem("tbf-dash-finance-open") !== "false"; }
    catch { return true; }
  });
  const [activityOpen, setActivityOpen] = useState(() => {
    try { return localStorage.getItem("tbf-dash-activity-open") === "true"; }
    catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("tbf-dash-finance-open",  String(financeOpen));  } catch {}
  }, [financeOpen]);
  useEffect(() => {
    try { localStorage.setItem("tbf-dash-activity-open", String(activityOpen)); } catch {}
  }, [activityOpen]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const h = { headers: getAuthHeaders() };
      const [bkRes, visaRes, frRes, vqRes, expRes, svcRes, corrRes, finRes] = await Promise.allSettled([
        axios.get(`${API}/bookings`, h),
        axios.get(`${API}/visa-requests`, h),
        axios.get(`${API}/flight-requests`, h),
        axios.get(`${API}/visa-quotes`, h),
        axios.get(`${API}/accounting/expenses?year=${CURRENT_YEAR}`, h),
        axios.get(`${API}/accounting/service-entries?year=${CURRENT_YEAR}`, h),
        axios.get(`${API}/accounting/vat-corrections`, h),
        axios.get(`${API}/accounting/dashboard?year=${CURRENT_YEAR}&quarter=${CURRENT_QUARTER}`, h),
      ]);
      if (bkRes.status === "fulfilled")   setBookings(bkRes.value.data || []);
      if (visaRes.status === "fulfilled") setVisaRequests(visaRes.value.data || []);
      if (frRes.status === "fulfilled")   setFlightRequests(frRes.value.data || []);
      if (vqRes.status === "fulfilled")   setVisaQuotes(vqRes.value.data || []);
      if (expRes.status === "fulfilled")  setExpenses(expRes.value.data || []);
      if (svcRes.status === "fulfilled")  setServiceEntries(svcRes.value.data || []);
      if (corrRes.status === "fulfilled") setVatCorrections(corrRes.value.data || []);
      if (finRes.status === "fulfilled")  setFinance(finRes.value.data);
      setLastRefresh(new Date());
    } catch (_) {}
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  // ── Derived counts ────────────────────────────────────────────────────────
  // Mirrors classifyBooking() in Bookings.js: prefer backend lifecycle_group,
  // fall back to last_segment_date / manual_past_travel.
  function classifyBooking(b) {
    if (b.lifecycle_group) return b.lifecycle_group;
    const st = (b.status || "").toLowerCase();
    if (st === "cancelled" || st === "refunded") return "cancelled";
    const ls = (b.lifecycle_status || "").toLowerCase();
    if (ls === "cancelled") return "cancelled";
    if (b.manual_past_travel || ls === "trip_completed" || ls === "closed") return "past";
    const lsd = b.last_segment_date || b.expiry_date;
    if (lsd) {
      const d = new Date(lsd + "T00:00:00");
      const today = new Date(); today.setHours(0, 0, 0, 0);
      if (d < today) return "past";
    }
    return "active";
  }

  const openBookings = bookings.filter(b => classifyBooking(b) === "active");

  const pendingTicketing = bookings.filter(b => {
    const s = (b.status || "").toLowerCase();
    const ts = (b.ticketing_status || "").toLowerCase();
    const hasTicket = (b.ticket_numbers || []).filter(Boolean).length > 0;
    return (s === "pending_ticketing" || ts === "pending_ticketing") && !hasTicket;
  });

  const openVisa = visaRequests.filter(v =>
    !["completed", "cancelled", "rejected"].includes((v.status || "").toLowerCase())
  );

  const pendingCustomerPayment = bookings.filter(b => {
    const accStatus = (b.accounting?.payment_status || b.payment_status || "").toLowerCase();
    return ["pending", "unpaid", "awaiting_payment"].includes(accStatus);
  });

  const pendingFlightRequests = flightRequests.filter(r =>
    (r.status || "").toLowerCase() === "pending"
  );

  // Visa quotes awaiting customer payment
  const awaitingPaymentVisa = visaQuotes.filter(vq => {
    const ps     = (vq.payment_status || "").toLowerCase();
    const status = (vq.status || "").toLowerCase();
    if (status === "cancelled") return false;
    if (ps === "paid") return false;
    // Explicitly awaiting payment via Tikkie/bank-transfer
    if (ps === "awaiting_payment" || ps === "pay_later") return true;
    // TBF number assigned (invoice_generated or work_started) but not yet paid
    if (vq.invoice_generated || vq.work_started) return true;
    return false;
  });

  const allAwaitingPaymentCount  = pendingCustomerPayment.length + awaitingPaymentVisa.length;
  const awaitingPaymentAmount    =
    pendingCustomerPayment.reduce((s, b) => s + parseFloat(b.selling_price || 0), 0) +
    awaitingPaymentVisa.reduce((s, vq) => s + parseFloat(vq.accounting?.invoice_total || vq.service_fee || 0), 0);

  const unpaidSupplier = bookings.filter(b => {
    const cost = b.base_price || 0;
    const paid = b.accounting?.supplier_paid_date || b.supplier_paid_date;
    return cost > 0 && !paid;
  });

  const missingReceipts = expenses.filter(e =>
    e.vat_rate > 0 && !e.receipt_url && !e.receipt_filename && !e.receipt_data
  );

  const vatNeedsReview = bookings.filter(b =>
    (b.accounting?.vat_type || "").includes("Needs Review")
  );

  const pendingCorrections = vatCorrections.filter(c => c.status === "pending");

  // ── Action items ─────────────────────────────────────────────────────────
  const actionItems = [];

  pendingTicketing.forEach(b => {
    const p = (b.passengers || [{}])[0];
    const name = `${p.first_name || ""} ${p.last_name || ""}`.trim() || b.id?.slice(0, 8);
    const inv = b.accounting?.invoice_number || b.id?.slice(0, 8);
    actionItems.push({
      type: "ticketing",
      name: `${inv} — ${name}`,
      reason: "Ticket not issued",
      amount: b.selling_price,
      go: () => navigate(`/bookings/${b.id}`),
    });
  });

  pendingCustomerPayment.slice(0, 5).forEach(b => {
    if (pendingTicketing.find(x => x.id === b.id)) return;
    const p = (b.passengers || [{}])[0];
    const name = `${p.first_name || ""} ${p.last_name || ""}`.trim() || b.id?.slice(0, 8);
    const inv = b.accounting?.invoice_number || b.id?.slice(0, 8);
    actionItems.push({
      type: "payment",
      name: `${inv} — ${name}`,
      reason: `Customer payment ${b.accounting?.payment_status || b.payment_status || "pending"}`,
      amount: b.selling_price,
      go: () => navigate(`/bookings/${b.id}`),
    });
  });

  awaitingPaymentVisa.slice(0, 4).forEach(vq => {
    const qNum = vq.quote_number || vq.accounting?.invoice_number || vq.id?.slice(0, 8);
    const ps   = vq.payment_status || "unpaid";
    const amt  = parseFloat(vq.accounting?.invoice_total || vq.service_fee || 0);
    actionItems.push({
      type:   "visa",
      name:   `${qNum} — ${vq.customer_name || "Visa customer"}`,
      reason: `Visa payment ${ps.replace(/_/g, " ")} · ${vq.destination_country || ""}`.trim().replace(/·\s*$/, ""),
      amount: amt || null,
      go:     () => navigate(`/visa-requests/${vq.visa_request_id || ""}`),
    });
  });

  unpaidSupplier.slice(0, 4).forEach(b => {
    const p = (b.passengers || [{}])[0];
    const name = `${p.first_name || ""} ${p.last_name || ""}`.trim() || b.id?.slice(0, 8);
    const inv = b.accounting?.invoice_number || b.id?.slice(0, 8);
    actionItems.push({
      type: "supplier",
      name: `${inv} — ${name}`,
      reason: "Supplier payment date not recorded",
      amount: b.base_price,
      go: () => navigate(`/bookings/${b.id}`),
    });
  });

  missingReceipts.slice(0, 3).forEach(e => {
    actionItems.push({
      type: "receipt",
      name: e.description || "Expense",
      reason: `${e.date} · ${e.category || ""} — VAT receipt missing`,
      amount: e.vat_amount,
      go: () => navigate("/accounting"),
    });
  });

  vatNeedsReview.slice(0, 3).forEach(b => {
    const inv = b.accounting?.invoice_number || b.id?.slice(0, 8);
    actionItems.push({
      type: "vat",
      name: inv,
      reason: "VAT type marked Needs Review",
      amount: null,
      go: () => navigate("/accounting"),
    });
  });

  pendingCorrections.slice(0, 3).forEach(c => {
    actionItems.push({
      type: "correction",
      name: `${c.original_period} → ${c.correction_period}`,
      reason: c.reason?.slice(0, 80) || "VAT carry-forward pending",
      amount: c.amount,
      go: () => navigate("/accounting"),
    });
  });

  openVisa
    .filter(v => ["pending_quote", "quote_requested"].includes((v.status || "").toLowerCase()))
    .slice(0, 3)
    .forEach(v => {
      const name = v.applicant_name || v.customer_name || "Applicant";
      actionItems.push({
        type: "visa",
        name,
        reason: `${v.visa_type || "Visa"} — ${v.destination_country || ""} · awaiting quote`,
        amount: null,
        go: () => navigate("/visa-requests"),
      });
    });

  // ── Recent activity feed ─────────────────────────────────────────────────
  const activity = [];

  bookings.slice(0, 8).forEach(b => {
    const p = (b.passengers || [{}])[0];
    const name = `${p.first_name || ""} ${p.last_name || ""}`.trim() || "—";
    const inv = b.accounting?.invoice_number || b.id?.slice(0, 8);
    activity.push({
      id: b.id,
      dotColor: "bg-violet-400",
      label: `Booking ${inv}`,
      sub: name,
      time: b.created_at,
      go: () => navigate(`/bookings/${b.id}`),
    });
  });

  visaRequests.slice(0, 4).forEach(v => {
    activity.push({
      id: v.id,
      dotColor: "bg-blue-400",
      label: `Visa — ${v.applicant_name || v.customer_name || "Applicant"}`,
      sub: `${v.visa_type || ""} ${v.destination_country ? "· " + v.destination_country : ""}`,
      time: v.created_at,
      go: () => navigate("/visa-requests"),
    });
  });

  expenses.slice(0, 4).forEach(e => {
    activity.push({
      id: e.id,
      dotColor: "bg-emerald-400",
      label: e.description || "Expense",
      sub: `${e.date} · ${fmt(e.amount)}`,
      time: e.created_at || e.date,
      go: () => navigate("/accounting"),
    });
  });

  serviceEntries.slice(0, 3).forEach(s => {
    activity.push({
      id: s.id,
      dotColor: "bg-orange-400",
      label: s.description || `Manual entry ${s.invoice_number || ""}`,
      sub: `${s.date} · ${fmt(s.amount)}`,
      time: s.created_at || s.date,
      go: () => navigate("/accounting"),
    });
  });

  activity.sort((a, b) => {
    try { return new Date(b.time) - new Date(a.time); } catch { return 0; }
  });
  const recentActivity = activity.slice(0, 8);

  const fmtTime = (t) => {
    try {
      const d = new Date(t);
      const now = new Date();
      const diffDays = Math.floor((now - d) / 86400000);
      if (diffDays === 0) return d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" });
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays}d ago`;
      return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
    } catch { return ""; }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 md:p-8 space-y-6 max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
              Control Panel
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {CURRENT_QUARTER} {CURRENT_YEAR}
              {lastRefresh && (
                <span className="ml-2 text-slate-300">
                  · updated {lastRefresh.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
            </p>
          </div>
          <button
            onClick={load}
            className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-purple-700 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-purple-50 hover:border-purple-200 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          {[
            { label: "Flight Request", icon: Plus,          path: "/requests" },
            { label: "Visa Request",   icon: Stamp,         path: "/visa-requests" },
            { label: "Add Expense",    icon: Receipt,       path: "/expenses/quick" },
            { label: "Accounting",     icon: BarChart2,     path: "/accounting" },
            { label: "VAT Report",     icon: FileSpreadsheet, path: "/accounting?tab=vat" },
          ].map(({ label, icon: Ic, path }) => (
            <button key={label}
              onClick={() => navigate(path)}
              className="flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 bg-white hover:bg-purple-50 hover:text-purple-700 hover:border-purple-200 transition-colors"
            >
              <Ic className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
        </div>

        {/* Status Cards — grouped */}
        <div className="space-y-5">

          {/* Operations */}
          <div>
            <SectionLabel>Operations</SectionLabel>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard
                label="Active / Upcoming"
                value={openBookings.length}
                sub="future or current travel"
                icon={Plane}
                status="neutral"
                onClick={() => navigate("/bookings")}
              />
              <StatCard
                label="Pending Ticketing"
                value={pendingTicketing.length}
                sub={pendingTicketing.length > 0 ? "needs ticket issuance" : "all clear"}
                icon={Stamp}
                status={pendingTicketing.length > 0 ? "warn" : "ok"}
                onClick={() => navigate("/bookings")}
              />
              <StatCard
                label="Pending Flight Requests"
                value={pendingFlightRequests.length}
                sub={pendingFlightRequests.length > 0 ? "awaiting agent action" : "all clear"}
                icon={Inbox}
                status={pendingFlightRequests.length > 0 ? "warn" : "ok"}
                onClick={() => navigate("/requests", { state: { statusFilter: "pending" } })}
              />
              <StatCard
                label="Open Visa Requests"
                value={openVisa.length}
                sub="in progress"
                icon={Users}
                status="neutral"
                onClick={() => navigate("/visa-requests")}
              />
            </div>
          </div>

          {/* Finance */}
          <div>
            <SectionLabel>Finance</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <StatCard
                label="Awaiting Payment"
                value={allAwaitingPaymentCount}
                sub={allAwaitingPaymentCount > 0
                  ? `${fmt(awaitingPaymentAmount)} outstanding`
                  : "all collected"}
                icon={CreditCard}
                status={allAwaitingPaymentCount > 0 ? "warn" : "ok"}
                onClick={() => navigate("/payments-due")}
              />
              <StatCard
                label="Supplier Payment Date Missing"
                value={unpaidSupplier.length}
                sub={unpaidSupplier.length > 0 ? "supplier payment not recorded" : "all recorded"}
                icon={Euro}
                status={unpaidSupplier.length > 0 ? "warn" : "ok"}
                onClick={() => navigate("/bookings")}
              />
              {finance ? (
                <StatCard
                  label={`Net Profit ${CURRENT_QUARTER}`}
                  value={fmt(finance.net_profit)}
                  sub="bookings – expenses"
                  icon={TrendingUp}
                  status={finance.net_profit >= 0 ? "ok" : "alert"}
                  onClick={() => navigate("/accounting")}
                />
              ) : (
                <StatCard
                  label={`Net Profit ${CURRENT_QUARTER}`}
                  value="—"
                  sub="unavailable"
                  icon={TrendingUp}
                  status="neutral"
                />
              )}
            </div>
          </div>

          {/* Compliance / VAT */}
          <div>
            <SectionLabel>Compliance / VAT</SectionLabel>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <StatCard
                label="Missing Receipts"
                value={missingReceipts.length}
                sub={missingReceipts.length > 0 ? "VAT expenses, no receipt" : "all receipts present"}
                icon={Receipt}
                status={missingReceipts.length > 0 ? "alert" : "ok"}
                onClick={() => navigate("/accounting")}
              />
              <StatCard
                label="VAT Needs Review"
                value={vatNeedsReview.length}
                sub={vatNeedsReview.length > 0 ? "bookings flagged" : "nothing flagged"}
                icon={ShieldAlert}
                status={vatNeedsReview.length > 0 ? "alert" : "ok"}
                onClick={() => navigate("/accounting")}
              />
              <StatCard
                label="Pending Corrections"
                value={pendingCorrections.length}
                sub={pendingCorrections.length > 0 ? "carry-forward pending" : "none outstanding"}
                icon={AlertCircle}
                status={pendingCorrections.length > 0 ? "warn" : "ok"}
                onClick={() => navigate("/accounting")}
              />
            </div>
          </div>

        </div>

        {/* Main grid: Actions + Finance + Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Action Required */}
          <div className="lg:col-span-2">
            <Card className={`border ${
              actionItems.length > 0
                ? "border-amber-200 bg-gradient-to-b from-amber-50/40 to-white"
                : "border-slate-200"
            }`}>
              <CardHeader className="pb-3 pt-5 px-5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2 text-slate-800">
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                      actionItems.length > 0 ? "bg-amber-100" : "bg-slate-100"
                    }`}>
                      <AlertCircle className={`w-4 h-4 ${actionItems.length > 0 ? "text-amber-600" : "text-slate-400"}`} />
                    </div>
                    Action Required
                    {actionItems.length > 0 && (
                      <span className="ml-1 bg-amber-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                        {actionItems.length}
                      </span>
                    )}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                {actionItems.length === 0 ? (
                  <div className="text-center py-10">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                      <CheckCircle className="w-6 h-6 text-emerald-500" />
                    </div>
                    <p className="text-sm font-semibold text-emerald-700">All clear</p>
                    <p className="text-xs text-slate-400 mt-1">Nothing needs your attention right now</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {actionItems.map((item, i) => (
                      <ActionItem key={i} {...item} onClick={item.go} />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right column: Finance + Activity */}
          <div className="space-y-5">

            {/* Financial Snapshot */}
            <Card className="border-purple-100 bg-gradient-to-b from-purple-50/30 to-white">
              <CardHeader
                className="pb-2 pt-5 px-5 cursor-pointer select-none"
                onClick={() => setFinanceOpen(o => !o)}
              >
                <CardTitle className="text-base font-semibold flex items-center justify-between text-slate-800">
                  <span className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                      <BarChart2 className="w-4 h-4 text-purple-500" />
                    </div>
                    Finance — {CURRENT_QUARTER} {CURRENT_YEAR}
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${financeOpen ? "" : "-rotate-90"}`}
                  />
                </CardTitle>
              </CardHeader>
              {financeOpen && <CardContent className="px-5 pb-5">
                {finance ? (
                  <div>
                    <FinanceRow label="Customer Invoiced"   value={finance.total_revenue}      />
                    <FinanceRow label="Supplier Cost"       value={finance.total_supplier_cost} />
                    <FinanceRow label="Gross Margin"        value={finance.gross_profit}        bold color={finance.gross_profit >= 0 ? "green" : "red"} />
                    <FinanceRow label="Operating Expenses"  value={-Math.abs(finance.total_operating_expenses || 0)} />
                    {(finance.total_allocated_expenses || 0) !== 0 && (
                      <FinanceRow label="Allocated Expenses (business share)" value={-Math.abs(finance.total_allocated_expenses)} />
                    )}
                    <FinanceRow
                      label="Net Profit"
                      value={finance.net_profit}
                      bold
                      color={finance.net_profit >= 0 ? "green" : "red"}
                      highlight
                    />
                    <div className="mt-4 pt-3 border-t border-slate-100 space-y-0">
                      <FinanceRow
                        label={
                          finance.vat_breakdown?.reclaimable_input_vat_filing != null &&
                          finance.vat_breakdown.reclaimable_input_vat_filing !== finance.vat_breakdown.reclaimable_input_vat
                            ? `Input VAT reclaimable (filing: €${finance.vat_breakdown.reclaimable_input_vat_filing})`
                            : "Input VAT reclaimable"
                        }
                        value={finance.vat_breakdown?.reclaimable_input_vat}
                      />
                      <FinanceRow label="VAT payable"           value={finance.vat_breakdown?.net_vat_payable} bold />
                    </div>
                    {pendingCorrections.length > 0 && (
                      <div className="mt-3 p-2.5 bg-amber-50 rounded-lg text-xs text-amber-800 border border-amber-100">
                        <span className="font-semibold">Pending correction:</span>{" "}
                        +{fmt(pendingCorrections.reduce((s, c) => s + (c.amount || 0), 0))} omzet carry-forward
                      </div>
                    )}
                    <Button
                      size="sm" variant="ghost"
                      className="w-full mt-3 text-xs text-slate-500 hover:text-slate-800 border border-slate-200 hover:border-slate-300"
                      onClick={() => navigate("/accounting")}
                    >
                      Open Accounting <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                ) : (
                  <div className="py-8 text-center">
                    <p className="text-sm text-slate-400">Finance data unavailable</p>
                  </div>
                )}
              </CardContent>}
            </Card>

            {/* Recent Activity */}
            <Card className="border-slate-200">
              <CardHeader
                className="pb-2 pt-5 px-5 cursor-pointer select-none"
                onClick={() => setActivityOpen(o => !o)}
              >
                <CardTitle className="text-base font-semibold flex items-center justify-between text-slate-800">
                  <span className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-slate-400" />
                    </div>
                    Recent Activity
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${activityOpen ? "" : "-rotate-90"}`}
                  />
                </CardTitle>
              </CardHeader>
              {activityOpen && <CardContent className="px-5 pb-4">
                {recentActivity.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-sm text-slate-400">No recent activity</p>
                  </div>
                ) : (
                  <div className="space-y-px">
                    {recentActivity.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-3 py-2 px-2 -mx-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors group"
                        onClick={item.go}
                      >
                        <div className="flex-shrink-0 mt-1">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ring-2 ring-white ${item.dotColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">{item.label}</p>
                          <p className="text-[11px] text-slate-400 truncate">{item.sub}</p>
                        </div>
                        <span className="text-[11px] text-slate-300 group-hover:text-slate-500 flex-shrink-0 mt-0.5 transition-colors">
                          {fmtTime(item.time)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>}
            </Card>

          </div>
        </div>

      </div>
    </DashboardLayout>
  );
}
